import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import Select, { components } from "react-select";
import { ChevronDown } from "lucide-react";
import ErrorMessage from "../Common/ErrorMessage";
import BackLink from "../Common/BackLink";
import Button from "../Common/Button";
import LocationAutocomplete from "../Common/LocationAutocomplete";
import { showFirstFormErrorToast } from "../../utils/formErrorToast";
import { getCountryList } from "../../api/postgre/registerApi";
import { defaultReactSelectStyles } from "../../utils/reactSelectStyles";

const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
        <ChevronDown className="h-4 w-4 text-slate-400" />
    </components.DropdownIndicator>
);

const checkoutSelectStyles = {
    ...defaultReactSelectStyles,
    control: (base, state) => ({
        ...defaultReactSelectStyles.control(base),
        borderRadius: "0.375rem",
        backgroundColor: "#f7f7f7",
        borderColor: state.isFocused ? "#0f1b8f" : "#E2E8F0",
        boxShadow: "none",
        outline: "none",
        minHeight: "48px",
        "&:hover": {
            borderColor: state.isFocused ? "#0f1b8f" : "#E2E8F0",
            backgroundColor: "#f7f7f7",
        },
        "&:active": {
            backgroundColor: "#f7f7f7",
        },
    }),
};

const CheckoutForm = ({ formData, onSubmit, onBack, isSubmitting, hideButton = false, onValidChange }) => {
    const { t } = useTranslation();
    const [countryOptions, setCountryOptions] = useState([]);

    useEffect(() => {
        getCountryList().then(setCountryOptions).catch(() => {});
    }, []);
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            street: "",
            number: "",
            floor: "",
            city: "",
            postalCode: "",
            area: "",
            country: "",
        },
    });

    useEffect(() => {
        onValidChange?.(isValid);
    }, [isValid, onValidChange]);

    useEffect(() => {
        if (!formData) return;
        reset({
            firstName: formData.firstName || "",
            lastName: formData.lastName || "",
            email: formData.email || "",
            phone: formData.phone || "",
            street: formData.street || "",
            number: formData.number || "",
            floor: formData.floor || "",
            city: formData.city || "",
            postalCode: formData.postalCode || "",
            area: typeof formData.area === "object" ? formData.area?.label || "" : formData.area || "",
            country: formData.country || "",
        });
    }, [formData, reset]);

    const handleFormSubmit = (values) => {
        onSubmit?.(values);
    };

    const handleFormInvalid = (formErrors) => {
        showFirstFormErrorToast(formErrors);
    };

    return (
        <div>
            <div className="flex items-center gap-3 text-xs font-semibold text-slate-500">
                <BackLink label={t("PRODUCT_COLLECTIONS_BACK_TO_COLLECTION")} onClick={onBack} className="inline-flex items-center gap-2 underline underline-offset-4" />
            </div>
            <h2 className="mt-6 text-lg font-semibold text-slate-900">{t("PRODUCT_COLLECTIONS_CHECKOUT_TITLE")}</h2>
            <p className="mt-1 text-sm text-slate-500">
                {t("PRODUCT_COLLECTIONS_CHECKOUT_SUBTITLE")}
            </p>

            <form id="checkout-form" className="mt-8 space-y-8" autoComplete="off" onSubmit={handleSubmit(handleFormSubmit, handleFormInvalid)}>
                <input type="text" autoComplete="username" className="hidden" tabIndex={-1} aria-hidden="true" />
                <input type="password" autoComplete="new-password" className="hidden" tabIndex={-1} aria-hidden="true" />
                <div>
                    <p className="text-sm font-semibold text-slate-900">{t("PRODUCT_COLLECTIONS_RECIPIENT_DETAILS")}</p>
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <div>
                            <Controller
                                name="firstName"
                                control={control}
                                rules={{ required: t("AUTH_VALIDATION_FIRST_NAME_REQUIRED") }}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        placeholder={t("AUTH_PLACEHOLDER_FIRST_NAME")}
                                        className="w-full rounded-md border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none"
                                    />
                                )}
                            />
                            <ErrorMessage message={errors.firstName?.message} />
                        </div>
                        <div>
                            <Controller
                                name="lastName"
                                control={control}
                                rules={{ required: t("AUTH_VALIDATION_LAST_NAME_REQUIRED") }}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        placeholder={t("AUTH_PLACEHOLDER_LAST_NAME")}
                                        className="w-full rounded-md border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none"
                                    />
                                )}
                            />
                            <ErrorMessage message={errors.lastName?.message} />
                        </div>
                        <div>
                            <Controller
                                name="email"
                                control={control}
                                rules={{
                                    required: t("AUTH_VALIDATION_EMAIL_REQUIRED"),
                                    pattern: {
                                        value: /\S+@\S+\.\S+/,
                                        message: t("AUTH_VALIDATION_EMAIL_INVALID"),
                                    },
                                }}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="email"
                                        placeholder={t("AUTH_PLACEHOLDER_EMAIL")}
                                        className="w-full rounded-md border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none"
                                    />
                                )}
                            />
                            <ErrorMessage message={errors.email?.message} />
                        </div>
                        <div>
                            <Controller
                                name="phone"
                                control={control}
                                rules={{
                                    required: t("AUTH_VALIDATION_PHONE_REQUIRED"),
                                    pattern: {
                                        value: /^\+?[\d\s\-().]{7,20}$/,
                                        message: t("AUTH_VALIDATION_PHONE_INVALID"),
                                    },
                                }}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="tel"
                                        placeholder={t("AUTH_PLACEHOLDER_PHONE")}
                                        className="w-full rounded-md border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none"
                                    />
                                )}
                            />
                            <ErrorMessage message={errors.phone?.message} />
                        </div>
                    </div>
                </div>

                <div>
                    <p className="text-sm font-semibold text-slate-900">{t("PRODUCT_COLLECTIONS_SHIPPING_ADDRESS")}</p>
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <div>
                            <Controller
                                name="street"
                                control={control}
                                rules={{ required: t("CHECKOUT_VALIDATION_STREET_REQUIRED") }}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        placeholder={t("CHECKOUT_PLACEHOLDER_STREET")}
                                        className="w-full rounded-md border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none"
                                    />
                                )}
                            />
                            <ErrorMessage message={errors.street?.message} />
                        </div>
                        <div>
                            <Controller
                                name="number"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        placeholder={t("CHECKOUT_PLACEHOLDER_NUMBER")}
                                        className="w-full rounded-md border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none"
                                    />
                                )}
                            />
                            <ErrorMessage message={errors.number?.message} />
                        </div>
                        <div>
                            <Controller
                                name="floor"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        placeholder={t("CHECKOUT_PLACEHOLDER_FLOOR")}
                                        className="w-full rounded-md border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none"
                                    />
                                )}
                            />
                            <ErrorMessage message={errors.floor?.message} />
                        </div>
                        <div>
                            <Controller
                                name="postalCode"
                                control={control}
                                rules={{ required: t("CHECKOUT_VALIDATION_POSTAL_CODE_REQUIRED") }}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        placeholder={t("CHECKOUT_PLACEHOLDER_POSTAL_CODE")}
                                        className="w-full rounded-md border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none"
                                    />
                                )}
                            />
                            <ErrorMessage message={errors.postalCode?.message} />
                        </div>
                        <div>
                            <Controller
                                name="city"
                                control={control}
                                rules={{ required: t("CHECKOUT_VALIDATION_CITY_REQUIRED") }}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        placeholder={t("CHECKOUT_PLACEHOLDER_CITY")}
                                        className="w-full rounded-md border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none"
                                    />
                                )}
                            />
                            <ErrorMessage message={errors.city?.message} />
                        </div>
                        <div>
                            <Controller
                                name="area"
                                control={control}
                                rules={{ required: t("CHECKOUT_VALIDATION_REGION_REQUIRED") }}
                                render={({ field }) => (
                                    <LocationAutocomplete
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder={t("CHECKOUT_PLACEHOLDER_REGION")}
                                    />
                                )}
                            />
                            <ErrorMessage message={errors.area?.message} />
                        </div>
                        <div>
                            <Controller
                                name="country"
                                control={control}
                                rules={{ required: t("AUTH_VALIDATION_COUNTRY_REQUIRED") }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        value={countryOptions.find((option) => option.value === field.value) || null}
                                        options={countryOptions}
                                        placeholder={t("AUTH_REGISTER_COUNTRY_PLACEHOLDER")}
                                        classNamePrefix="react-select"
                                        components={{ DropdownIndicator }}
                                        styles={checkoutSelectStyles}
                                        isSearchable={false}
                                        onChange={(selected) => field.onChange(selected?.value || "")}
                                    />
                                )}
                            />
                            <ErrorMessage message={errors.country?.message} />
                        </div>
                    </div>
                </div>

                {!hideButton && (
                    <Button
                        type="submit"
                        disabled={!isValid || isSubmitting}
                        className="mt-2 w-full"
                    >
                        {isSubmitting ? t("COMMON_SUBMITTING") : t("PRODUCT_COLLECTIONS_COMPLETE_ORDER")}
                    </Button>
                )}
            </form>
        </div>
    );
};

export default CheckoutForm;
